import React, { useState } from 'react';
import { Box, TextField, Button, Alert, CircularProgress } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { changePassword } from '../services/userSettingsService';

/**
 * Form component for changing user password
 * Shows button first, then form fields when clicked
 */
const ChangePasswordForm = ({ onSuccess, onError }) => {
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('success');

    // Handle showing the form
    const handleShowForm = () => {
        setShowForm(true);
    };

    // Handle canceling form
    const handleCancel = () => {
        setShowForm(false);
        setFormData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setErrors({});
        setShowAlert(false);
    };

    // Handle input changes
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear field-specific error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }

        // Clear alert when user makes changes
        if (showAlert) {
            setShowAlert(false);
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.currentPassword.trim()) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!formData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'New password must be at least 6 characters';
        }

        if (!formData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Please confirm your new password';
        } else if (formData.confirmPassword !== formData.newPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            await changePassword({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword,
            });

            // Success - clear form and show success message
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            });

            setAlertMessage('Password changed successfully');
            setAlertSeverity('success');
            setShowAlert(true);

            // Hide form after success
            setTimeout(() => {
                setShowForm(false);
                setShowAlert(false);
            }, 2000);

            // Call parent success callback if provided
            if (onSuccess) {
                onSuccess('Password changed successfully');
            }
        } catch (error) {
            console.error('Error changing password:', error);

            let errorMessage = 'Unable to change password at this time. Please try again later.';

            if (error.response?.data && typeof error.response.data === 'string') {
                errorMessage = error.response.data;
            } else if (error.response?.status === 400) {
                errorMessage = 'Current password is incorrect';
            }

            setAlertMessage(errorMessage);
            setAlertSeverity('error');
            setShowAlert(true);

            // Call parent error callback if provided
            if (onError) {
                onError(errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // If form is not shown, show the button
    if (!showForm) {
        return (
            <Box>
                <Button
                    variant="outlined"
                    onClick={handleShowForm}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: '#e0e0e0',
                        color: '#5b616e',
                        '&:hover': {
                            borderColor: '#1652f0',
                            backgroundColor: 'rgba(22, 82, 240, 0.04)',
                        },
                    }}
                >
                    Change Password
                </Button>
            </Box>
        );
    }

    // Show the form
    return (
        <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid2 container spacing={3}>
                {/* Alert */}
                {showAlert && (
                    <Grid2 size={{ xs: 12 }}>
                        <Alert
                            severity={alertSeverity}
                            onClose={() => setShowAlert(false)}
                            sx={{ borderRadius: '8px' }}
                        >
                            {alertMessage}
                        </Alert>
                    </Grid2>
                )}

                {/* Current Password */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        name="currentPassword"
                        type="password"
                        label="Current Password"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        error={!!errors.currentPassword}
                        helperText={errors.currentPassword}
                        fullWidth
                        required
                        disabled={loading}
                        size="small"
                    />
                </Grid2>

                {/* New Password */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        name="newPassword"
                        type="password"
                        label="New Password"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        fullWidth
                        required
                        disabled={loading}
                        size="small"
                    />
                </Grid2>

                {/* Confirm New Password */}
                <Grid2 size={{ xs: 12 }}>
                    <TextField
                        name="confirmPassword"
                        type="password"
                        label="Confirm New Password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        fullWidth
                        required
                        disabled={loading}
                        size="small"
                    />
                </Grid2>

                {/* Action Buttons */}
                <Grid2 size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                backgroundColor: '#1652f0',
                                '&:hover': {
                                    backgroundColor: '#1445d8',
                                },
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                            }}
                        >
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                    Changing...
                                </Box>
                            ) : (
                                'Change Password'
                            )}
                        </Button>

                        <Button
                            type="button"
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={loading}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: '#e0e0e0',
                                color: '#5b616e',
                            }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Grid2>
            </Grid2>
        </Box>
    );
};

export default ChangePasswordForm;
